'use client';

// Global
import { useEffect, RefObject, useCallback } from 'react';

const useClickOutside = (
  ref: RefObject<HTMLElement | null>,
  condition: boolean,
  /**
   * Receives the originating `mousedown`, so a caller can inspect what was pressed and decline
   * to act. Callers that do not care can keep taking no arguments.
   */
  callback: (event: MouseEvent) => void
) => {
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback(event);
      }
    },
    [callback, ref]
  );

  useEffect(() => {
    if (condition) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [condition, handleClickOutside]);
};

export default useClickOutside;
